import { Box, Button, Grid, Tab, Tabs } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Spinner from 'components/shared/Spinner';
import { UserContext } from 'components/UserGuard';
import { OrderPayment } from 'store/types/Payment';
import { defaultGridContainerProps, defaultGridItemProps, getPageTitle } from 'util/Layout';
import useRequest from 'hooks/useRequest';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import { ConfigContext } from 'components/ConfigGuard';
import MakePaymentPageView from 'components/payments/MakePaymentPageView';
import OrderHistoryPageView from 'components/payments/OrderHistoryPageView';
import CompanyService from 'services/api/CompanyService';
import routes from 'store/configs/Routes';
import { useHistory, useLocation } from 'react-router';
import CompanySelect from 'components/company/CompanySelect';
import PartnerPayButton from 'components/payments/PartnerPayButton';
import ButtonGroupWithBadge, { ButtonGroupWithBadgeItem } from 'components/shared/ButtonGroupWithBadge';
import SiteModule from 'store/enums/SiteModule';
import {
  ALL_COMPANIES_DROPDOWN_KEY,
  SELECT_COMPANY_DROPDOWN_KEY,
  getCompanySelectOptions,
  CompanySelectOption,
  ALL_PROGRAMS_OPTION,
} from 'util/Company';
import Tile from 'components/shared/Tile';
import { getFormattedOrdersList, getPrice, preselectedOrdersStatePropName } from 'util/Payment';
import { CompanyPaymentDetails } from 'store/types/Company';
import DownloadStatementModal from 'components/payments/DownloadStatementModal';
import ApplyCreditsToggle from 'components/payments/ApplyCreditsToggle';
import { UserPrograms } from 'store/types/User';
import { ParametersContext } from 'components/ParametersGuard';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';
import styles from './CompanyPaymentPage.module.scss';

const tabConfig = {
  CompanyPayments: { key: 'CompanyPayments', label: 'Company Invoices', route: routes.companyPayments } as TabItem,
  MakeCompanyPayment: {
    key: 'MakeCompanyPayment',
    label: 'Pay Company Invoices',
    route: routes.makeCompanyPayment,
  } as TabItem,
  MakeCompanySalesPayment: {
    key: 'MakeCompanySalesPayment',
    label: 'Pay Company Sales Orders',
    route: routes.makeCompanySalesPayment,
  } as TabItem,
};

const getSubsidiaryButtonItems = (invoices: OrderPayment[] | undefined, companyId: string) => {
  if (!invoices) return [];
  const unpaidInvoices = getFormattedOrdersList(invoices, true);
  if (!unpaidInvoices) return [];
  const filteredUnpaidInvoices = unpaidInvoices.filter((item) => item.ownerId === companyId);

  const map = new Map<string, ButtonGroupWithBadgeItem>();

  for (const unpaidInvoice of filteredUnpaidInvoices) {
    const buttonLabel = (unpaidInvoice.subsidiaryDisplayName || unpaidInvoice.subsidiaryName) ?? '';

    if (!unpaidInvoice?.subsidiaryId || !buttonLabel) {
      continue;
    }

    const key = unpaidInvoice.subsidiaryId;
    const existing = map.get(key);

    if (existing?.count) {
      existing.count += 1;
    } else {
      const buttonItem: ButtonGroupWithBadgeItem = {
        id: unpaidInvoice.subsidiaryId,
        label: buttonLabel,
        count: 1,
      };
      map.set(key, buttonItem);
    }
  }

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const CompanyPaymentsPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { state } = useLocation<{ [preselectedOrdersStatePropName]?: string[] }>();
  const { functions = [], userLoading, userPermissions } = useContext(UserContext);
  const {
    sidebar,
    isNccerTheme,
    isSmacnaTheme,
    modulesSettings: {
      companyPayments: { enablePaySalesOrders, hiddenTabs, partnerPayLink },
    },
  } = useContext(ConfigContext);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedProgramId, setSelectedProgramId] = useState<string>(ALL_PROGRAMS_OPTION);
  const [downloadStatementModalOpen, setDownloadStatementModalOpen] = useState<boolean>(false);
  const [showSelectCompanyMenuItem, setShowSelectCompanyMenuItem] = useState<boolean | null>(null);
  const [subsidiaryId, setSubsidiaryId] = useState<string>('');
  const permissions: string[] = useMemo(
    () => userPermissions.modules.find(({ name }) => name === SiteModule.Company)?.permissions || [],
    [userPermissions]
  );
  const {
    companyManagement: { companyDropdownName },
  } = useContext(ParametersContext);

  const options: CompanySelectOption[] = useMemo(
    () =>
      showSelectCompanyMenuItem !== null
        ? getCompanySelectOptions(functions, permissions, false, showSelectCompanyMenuItem)
        : [],
    [functions, permissions, showSelectCompanyMenuItem]
  );

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const ordersReqAllCompanies = useMemo(
    () => () => CompanyService.getAllCompanyOrders(selectedProgramId === ALL_PROGRAMS_OPTION ? '' : selectedProgramId),
    [selectedProgramId]
  );

  const companyDetailsRequest = useMemo(
    () =>
      selectedCompanyId &&
      selectedCompanyId !== ALL_COMPANIES_DROPDOWN_KEY &&
      selectedCompanyId !== SELECT_COMPANY_DROPDOWN_KEY
        ? () => CompanyService.getCompanyPaymentDetails(selectedCompanyId)
        : undefined,
    [selectedCompanyId]
  );

  const companyDetailsRequestData = useRequest<CompanyPaymentDetails>(companyDetailsRequest);
  const { data: companyDetailData, loading: companyDetailLoading } = companyDetailsRequestData;
  const userPrograms: UserPrograms[] = useMemo(() => companyDetailData?.programs || [], [companyDetailData?.programs]);
  const balanceDue: number | undefined = useMemo(() => companyDetailData?.balance, [companyDetailData]);
  const ordersReqDataAllCompanies = useRequest<OrderPayment[]>(ordersReqAllCompanies);
  const { data: orderDataAllCompanies, firstLoading: ordersFirstLoadingAllCompanies } = ordersReqDataAllCompanies;
  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);

  const subsidiaryButtonItems = useMemo(
    () => getSubsidiaryButtonItems(orderDataAllCompanies, selectedCompanyId),
    [orderDataAllCompanies, selectedCompanyId]
  );

  useEffect(() => {
    setSelectedProgramId(ALL_PROGRAMS_OPTION);
    if (activeTab === tabConfig.MakeCompanyPayment.key || activeTab === tabConfig.MakeCompanySalesPayment.key) {
      setShowSelectCompanyMenuItem(true);
      setSubsidiaryId('');
    } else {
      setShowSelectCompanyMenuItem(false);
    }
  }, [activeTab, selectedCompanyId]);

  const handleCompanyChange = useCallback(
    (newCompanyId: string) => {
      setSelectedCompanyId(newCompanyId);
      if (state) {
        history.replace({ pathname, state: null });
      }
    },
    [history, pathname, state]
  );

  const handleProgramSelectChange = useCallback((value: string) => {
    setSelectedProgramId(value);
  }, []);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.CompanyPayments.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, visibleTabs]);

  const handleDownloadStatementModalOpen = useCallback(() => {
    setDownloadStatementModalOpen(true);
  }, []);

  const handleDownloadStatementModalClose = useCallback(() => {
    setDownloadStatementModalOpen(false);
  }, []);

  useEffect(() => {
    if (subsidiaryButtonItems.length === 1) {
      setSubsidiaryId(subsidiaryButtonItems[0].id);
    }
  }, [subsidiaryButtonItems]);

  const handleSubsidiaryChange = (id: string) => {
    setSubsidiaryId(id === subsidiaryId ? '' : id);
  };

  const getTabPanelContent = useCallback(
    (children: React.ReactNode, displayApplyCreditToggle?: boolean): React.ReactNode => (
      <Grid {...defaultGridContainerProps} justifyContent={'flex-end'}>
        {isNccerTheme && selectedCompanyId !== ALL_COMPANIES_DROPDOWN_KEY && (
          <Grid {...defaultGridItemProps} xs={true}>
            <div className={styles.tilesWrapper}>
              <Tile
                value={getPrice(balanceDue)}
                label={'Account Balance'}
                classes={{ value: styles.value, label: styles.label }}
                loading={companyDetailLoading}
                theme={'dark-red'}
                className={styles.tile}
              />
              <span className={styles.helperText}>
                {'A negative account balance indicates that there is a credit on the account.'}
              </span>
            </div>
          </Grid>
        )}
        {selectedCompanyId === ALL_COMPANIES_DROPDOWN_KEY && partnerPayLink ? (
          <Grid {...defaultGridItemProps} xs={'auto'} alignItems={'left'} marginRight={'auto'}>
            <PartnerPayButton label={partnerPayLink.label} link={partnerPayLink.link} />
          </Grid>
        ) : (
          <>
            {partnerPayLink && (
              <Grid {...defaultGridItemProps} xs={'auto'} marginRight={'auto'}>
                <PartnerPayButton label={partnerPayLink.label} link={partnerPayLink.link} />
              </Grid>
            )}
            {displayApplyCreditToggle && (
              <Grid {...defaultGridItemProps} xs={'auto'}>
                <ApplyCreditsToggle id={selectedCompanyId} {...companyDetailsRequestData} />
              </Grid>
            )}
            {subsidiaryButtonItems.length === 0 ||
              (subsidiaryId && (
                <Grid {...defaultGridItemProps} xs={'auto'}>
                  <Button variant={'contained'} color={'secondary'} onClick={handleDownloadStatementModalOpen}>
                    {'Generate Statement'}
                  </Button>
                </Grid>
              ))}
          </>
        )}
        <Grid {...defaultGridItemProps}>{children}</Grid>
      </Grid>
    ),
    [
      isNccerTheme,
      selectedCompanyId,
      balanceDue,
      companyDetailLoading,
      partnerPayLink,
      companyDetailsRequestData,
      subsidiaryButtonItems.length,
      subsidiaryId,
      handleDownloadStatementModalOpen,
    ]
  );

  return (
    <>
      <DownloadStatementModal
        {...companyDetailsRequestData}
        open={downloadStatementModalOpen}
        id={selectedCompanyId}
        onClose={handleDownloadStatementModalClose}
      />
      <h1 className={commonStyles.pageTitle}>
        {getPageTitle(sidebar, SiteModule.Payments, SiteModule.CompanyPayments, !isSmacnaTheme) || 'Manage Company'}
      </h1>
      <Spinner loading={userLoading} transparent={false}>
        <TabContext value={activeTab}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor={'primary'}
            textColor={'primary'}
            variant={'scrollable'}
          >
            {visibleTabs.map((tab) => {
              if (tab.key === tabConfig.MakeCompanySalesPayment.key && !enablePaySalesOrders) {
                return;
              }

              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={tab.label}
                  className={commonStyles.tab}
                  classes={{ selected: commonStyles.activeTab }}
                />
              );
            })}
          </Tabs>
          <div className={styles.companySelect}>
            {showSelectCompanyMenuItem !== null && (
              <CompanySelect
                options={options}
                onChange={handleCompanyChange}
                size={'small'}
                label={companyDropdownName || 'Select Company'}
                showAllCompaniesMenuItem={
                  (activeTab === tabConfig.MakeCompanyPayment.key ||
                    activeTab === tabConfig.MakeCompanySalesPayment.key) &&
                  showSelectCompanyMenuItem
                }
              />
            )}
          </div>
          <TabPanel className={commonStyles.tabPanel} value={tabConfig.CompanyPayments.key}>
            {getTabPanelContent(
              !!selectedCompanyId && (
                <Spinner loading={ordersFirstLoadingAllCompanies} transparent={false}>
                  <OrderHistoryPageView
                    {...ordersReqDataAllCompanies}
                    companyId={selectedCompanyId}
                    makePaymentRoute={routes.makeCompanyPayment}
                    openOrdersEnabled={true}
                    onProgramChange={handleProgramSelectChange}
                    programId={selectedProgramId}
                    programsList={userPrograms}
                  />
                </Spinner>
              ),
              true
            )}
          </TabPanel>
          <TabPanel className={commonStyles.tabPanel} value={tabConfig.MakeCompanyPayment.key}>
            <Grid {...defaultGridContainerProps}>
              <Grid {...defaultGridItemProps}>
                {selectedCompanyId !== ALL_COMPANIES_DROPDOWN_KEY && subsidiaryButtonItems.length > 1 && (
                  <Box sx={{ marginBottom: 4 }}>
                    <ButtonGroupWithBadge
                      items={subsidiaryButtonItems}
                      selectedId={subsidiaryId}
                      showZeroCounts={true}
                      size={'large'}
                      ungrouped={true}
                      onChange={handleSubsidiaryChange}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>

            {getTabPanelContent(
              <>
                <Spinner loading={ordersFirstLoadingAllCompanies} transparent={false}>
                  <MakePaymentPageView
                    {...ordersReqDataAllCompanies}
                    companyId={selectedCompanyId}
                    subsidiaryId={subsidiaryId}
                    onProgramChange={handleProgramSelectChange}
                    programId={selectedProgramId}
                    hasMultiSubsidiaries={subsidiaryButtonItems.length > 1}
                  />
                </Spinner>
              </>
            )}
          </TabPanel>
          <TabPanel className={commonStyles.tabPanel} value={tabConfig.MakeCompanySalesPayment.key}>
            {getTabPanelContent(
              <Spinner loading={ordersFirstLoadingAllCompanies} transparent={false}>
                <MakePaymentPageView
                  {...ordersReqDataAllCompanies}
                  companyId={selectedCompanyId}
                  subsidiaryId={subsidiaryId}
                  onProgramChange={handleProgramSelectChange}
                  programId={selectedProgramId}
                  salesOrders={true}
                />
              </Spinner>
            )}
          </TabPanel>
        </TabContext>
      </Spinner>
    </>
  );
};

export default CompanyPaymentsPage;
